import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Search, Globe, ShieldCheck, Phone, UserCheck, ArrowRight } from "lucide-react";
import { HeroVideo } from "@/components/site/HeroVideo";
import { Magnetic } from "@/components/site/Magnetic";
import { RevealLines, FadeUp, Overline } from "@/components/site/Reveal";
import { LaptopFrame, PhoneFrame } from "@/components/site/DeviceMockup";
import RoiCalculator from "@/components/site/RoiCalculator";
import { PROJECTS } from "@/lib/data";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="home-hero dark-section relative overflow-hidden bg-[#0A0A0A]">
      <div data-testid="hero-media" className="home-hero-media">
      <motion.div style={{ y: reduceMotion ? 0 : y }} className="absolute inset-0">
        <HeroVideo />
      </motion.div>
      <div data-testid="hero-video-overlay" className="home-hero-overlay pointer-events-none absolute inset-0" />
      </div>

      <motion.div data-testid="hero-content" className="home-hero-content container-x relative flex flex-col items-center text-center text-white md:items-start md:text-left">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
          className="text-xs font-medium tracking-normal text-white/75"
        >
          Mahseri Web · Website design
        </motion.p>

        <h1 data-testid="hero-heading" className="font-display mt-3 max-w-lg text-3xl font-semibold leading-[1.1] tracking-normal sm:text-4xl lg:text-5xl">
          {["Your business,", "well presented."].map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.4 + i * 0.14, duration: 1, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-description"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
          className="mt-4 max-w-sm text-sm leading-relaxed text-white/80"
        >
          Thoughtful websites that help people understand what you do — and take the next step.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8, ease: EASE }}
          data-testid="hero-actions"
          className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 md:justify-start"
        >
          <Magnetic strength={0.2}>
            <Link
              to="/contact"
              data-testid="hero-build-my-website-btn"
              className="inline-flex min-h-11 items-center gap-3 rounded-full bg-white px-5 py-3 text-sm font-semibold tracking-normal text-black transition-transform duration-300 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
            >
              Let’s talk <ArrowUpRight size={16} />
            </Link>
          </Magnetic>
          <Magnetic strength={0.2}>
            <Link
              to="/work"
              data-testid="hero-see-our-work-btn"
              className="group inline-flex min-h-11 items-center gap-2 py-3 text-sm font-medium tracking-normal text-white/85 transition-colors duration-300 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
            >
              Explore examples <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Magnetic>
        </motion.div>

      </motion.div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div className="dark-section overflow-hidden border-y border-white/10 bg-[#0A0A0A] py-6 text-white">
    <Marquee speed={40} autoFill>
      {["Look Professional", "Build Trust", "Get Found", "Convert Visitors", "Grow Faster"].map((t) => (
        <span key={t} className="font-display mx-8 flex items-center gap-8 text-2xl font-semibold uppercase tracking-tight md:text-3xl">
          {t} <span className="text-white/30">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const PROBLEMS = [
  { num: "01", title: "You look less credible", desc: "If customers can't find a professional website, they may question whether your business is established or trustworthy." },
  { num: "02", title: "Your competitors look better", desc: "Customers compare before they choose. A weak online presence can make an otherwise great business look less professional." },
  { num: "03", title: "You're making customers work too hard", desc: "If people have to dig through Instagram, Facebook, and DMs just to find your services or contact info, you're creating friction." },
];

const ProblemSection = () => (
  <section data-testid="home-problem-section" className="bg-[#FAFAFA]">
    <div className="container-x section-pad">
      <Overline>Chapter 01 — The Problem</Overline>
      <h2 className="font-display mt-6 max-w-5xl text-4xl font-semibold uppercase leading-[1.02] tracking-tight md:text-5xl lg:text-[4vw]">
        <RevealLines lines={["You might be losing", "customers before they", "ever call you."]} />
      </h2>
      <FadeUp delay={0.15}>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-black/60 md:text-lg">
          Customers research businesses online before they ever pick up the phone. What they find decides who gets the call.
        </p>
      </FadeUp>

      <div className="mt-16 grid gap-5 md:grid-cols-3">
        {PROBLEMS.map((p, i) => (
          <motion.div
            key={p.num}
            data-testid={`problem-card-${p.num}`}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
            className="group rounded-2xl border border-black/10 p-8 transition-colors duration-500 hover:bg-black hover:text-white md:p-10"
          >
            <span className="font-display text-sm font-bold text-black/30 transition-colors duration-500 group-hover:text-white/40">{p.num}</span>
            <h3 className="font-display mt-16 text-2xl font-semibold uppercase leading-tight tracking-tight md:mt-24">{p.title}</h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-black/60 transition-colors duration-500 group-hover:text-white/60">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const JOURNEY = [
  { icon: Search, label: "Search" },
  { icon: Globe, label: "Website" },
  { icon: ShieldCheck, label: "Trust" },
  { icon: Phone, label: "Contact" },
  { icon: UserCheck, label: "Customer" },
];

const BigIdea = () => (
  <section data-testid="home-big-idea-section" className="dark-section bg-[#0A0A0A] text-white">
    <div className="container-x section-pad">
      <Overline light>Chapter 02 — The Big Idea</Overline>
      <h2 className="font-display mt-6 max-w-5xl text-4xl font-semibold uppercase leading-[1.02] tracking-tight md:text-5xl lg:text-[4.5vw]">
        <RevealLines lines={["Your website is your", "digital front door."]} />
      </h2>
      <FadeUp delay={0.15}>
        <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg">
          Every Google search, Instagram profile, ad, referral, and recommendation can lead customers to your business. When they arrive, your website has one job: <span className="font-medium text-white">make them want to take the next step.</span>
        </p>
      </FadeUp>

      <div className="mt-20 flex flex-col items-stretch gap-0 md:flex-row md:items-center">
        {JOURNEY.map((step, i) => (
          <div key={step.label} className="flex flex-col items-center md:flex-1 md:flex-row">
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.6, delay: i * 0.25, ease: EASE }}
              className="flex flex-col items-center gap-3"
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-[#111]">
                <step.icon size={26} strokeWidth={1.5} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">{step.label}</span>
            </motion.div>
            {i < JOURNEY.length - 1 && (
              <motion.div
                initial={{ scaleX: 0, scaleY: 0 }}
                whileInView={{ scaleX: 1, scaleY: 1 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.5, delay: i * 0.25 + 0.2, ease: EASE }}
                className="my-3 h-8 w-px origin-top bg-white/25 md:mx-2 md:my-0 md:mb-8 md:h-px md:flex-1 md:origin-left"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FEATURES = [
  { title: "Look Professional", desc: "A premium online presence that immediately communicates credibility." },
  { title: "Get Found", desc: "A website structured with search visibility and local SEO in mind." },
  { title: "Convert Visitors", desc: "Clear messaging, strong calls-to-action, testimonials, and trust signals." },
  { title: "Make It Easy To Contact You", desc: "Calls, forms, bookings, quote requests, and other conversion paths." },
];

const WhatWeBuild = () => (
  <section data-testid="home-features-section" className="bg-[#FAFAFA]">
    <div className="container-x section-pad">
      <Overline>Chapter 03 — What We Build</Overline>
      <h2 className="font-display mt-6 max-w-5xl text-4xl font-semibold uppercase leading-[1.02] tracking-tight md:text-5xl lg:text-[3.6vw]">
        <RevealLines lines={["We build digital experiences", "that help businesses", "win customers."]} />
      </h2>

      <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/10 sm:grid-cols-2">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
            className="group bg-[#FAFAFA] p-8 transition-colors duration-500 hover:bg-black hover:text-white md:p-12"
          >
            <ArrowRight size={20} className="text-black/30 transition-all duration-500 group-hover:translate-x-1 group-hover:text-white" />
            <h3 className="font-display mt-8 text-xl font-semibold uppercase tracking-tight md:text-2xl">{f.title}</h3>
            <p className="mt-3 text-sm font-light leading-relaxed text-black/60 transition-colors duration-500 group-hover:text-white/60">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const WorkPreview = () => (
  <section data-testid="home-work-section" className="bg-[#FAFAFA]">
    <div className="container-x pb-24 md:pb-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Overline>Chapter 04 — The Work</Overline>
          <h2 className="font-display mt-6 text-4xl font-semibold uppercase leading-none tracking-tight md:text-5xl lg:text-[4vw]">
            <RevealLines lines={["Examples of how", "your website", "could look."]} />
          </h2>
        </div>
        <FadeUp>
          <Magnetic strength={0.2}>
            <Link
              to="/work"
              data-testid="home-see-all-work-btn"
              className="inline-flex items-center gap-2 rounded-full border border-black px-7 py-3.5 text-sm font-semibold uppercase tracking-wide transition-colors duration-300 hover:bg-black hover:text-white"
            >
              See What We Can Build For You <ArrowUpRight size={16} />
            </Link>
          </Magnetic>
        </FadeUp>
      </div>

      <div className="mt-16 grid gap-10 md:grid-cols-2">
        {PROJECTS.slice(0, 4).map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: EASE }}
            className={`group ${i % 2 === 1 ? "md:mt-16" : ""}`}
          >
            <Link to="/work" data-testid={`home-project-${p.id}`}>
              <div className="overflow-hidden rounded-2xl">
                <div className="transition-transform duration-700 ease-out group-hover:scale-[1.03]">
                  <div className="relative bg-[#EDEDED] p-8 pt-10 md:p-12">
                    <LaptopFrame src={p.image} alt={p.name} />
                    <div className="absolute bottom-6 right-6 w-16 md:w-20">
                      <PhoneFrame src={p.image} alt={p.name} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 flex flex-col items-start justify-between gap-3 xl:flex-row xl:gap-4">
                <div>
                  <h3 className="font-display text-xl font-semibold uppercase tracking-tight">{p.name}</h3>
                  <p className="mt-1 text-sm font-light text-black/55">{p.solution}</p>
                </div>
                <span className="whitespace-nowrap rounded-full border border-black/15 px-3 py-1 text-[11px] uppercase tracking-widest text-black/60">{p.category}</span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const DIFFS = ["Custom design", "Conversion-focused structure", "Mobile-first experience", "Built around your business goals"];

const WhyUs = () => (
  <section data-testid="home-why-us-section" className="bg-[#FAFAFA]">
    <div className="container-x section-pad">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
        <div>
          <Overline>Chapter 05 — Why Us</Overline>
          <h2 className="font-display mt-6 text-4xl font-semibold uppercase leading-[1.02] tracking-tight md:text-5xl">
            <RevealLines lines={["We don't build websites", "just to make them", "look good."]} />
          </h2>
          <FadeUp delay={0.15}>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-black/60 md:text-lg">
              We build them around your business, your customers, and the action you want visitors to take.
            </p>
          </FadeUp>
        </div>
        <div className="flex flex-col justify-end">
          {DIFFS.map((d, i) => (
            <motion.div
              key={d}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
              className="flex items-center gap-5 border-b border-black/10 py-6"
            >
              <span className="font-display text-sm font-bold text-black/30">0{i + 1}</span>
              <span className="font-display text-lg font-semibold uppercase tracking-tight md:text-2xl">{d}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PROCESS = [
  { num: "01", title: "Discover", desc: "We learn about your business, customers, services, competitors, and goals." },
  { num: "02", title: "Design", desc: "We create a custom digital experience around your brand." },
  { num: "03", title: "Build", desc: "We develop the website and optimize it for desktop, tablet, and mobile." },
  { num: "04", title: "Launch", desc: "Your new website goes live and becomes your 24/7 digital storefront." },
];

const Process = () => (
  <section data-testid="home-process-section" className="dark-section bg-[#0A0A0A] text-white">
    <div className="container-x section-pad">
      <Overline light>Chapter 06 — The Process</Overline>
      <h2 className="font-display mt-6 text-4xl font-semibold uppercase leading-none tracking-tight md:text-5xl lg:text-[4vw]">
        <RevealLines lines={["Simple on purpose."]} />
      </h2>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:mt-16 lg:grid-cols-4">
        {PROCESS.map((s, i) => (
          <motion.div
            key={s.num}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.8, delay: i * 0.15, ease: EASE }}
            className="process-step rounded-2xl border border-white/10 bg-[#111] p-6 sm:p-8"
            style={{ "--step-offset": `${i * 12}px` }}
          >
            <span className="font-display text-5xl font-bold text-white/15">{s.num}</span>
            <h3 className="font-display mt-10 text-xl font-semibold uppercase tracking-tight">{s.title}</h3>
            <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="home-final-cta-section" className="dark-section border-b border-white/10 bg-[#0A0A0A] text-white">
    <div className="container-x flex min-h-[80vh] flex-col items-start justify-center py-24">
      <Overline light>Chapter 07 — Your Move</Overline>
      <h2 className="font-display mt-8 text-5xl font-bold uppercase leading-[0.95] tracking-tighter md:text-7xl lg:text-[6.5vw]">
        <RevealLines lines={["Ready to look like", "the business", "you're building?"]} />
      </h2>
      <FadeUp delay={0.2}>
        <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">
          Let's build a website that makes your business impossible to overlook.
        </p>
      </FadeUp>
      <FadeUp delay={0.3} className="mt-10 flex flex-wrap gap-4">
        <Magnetic strength={0.2}>
          <Link
            to="/contact"
            data-testid="final-cta-start-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white px-9 py-4 text-sm font-semibold uppercase tracking-wide text-black transition-transform duration-300 hover:scale-105"
          >
            Start My Website <ArrowUpRight size={16} />
          </Link>
        </Magnetic>
        <Magnetic strength={0.2}>
          <Link
            to="/contact"
            data-testid="final-cta-book-call-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-9 py-4 text-sm font-semibold uppercase tracking-wide transition-colors duration-300 hover:bg-white hover:text-black"
          >
            Book A Call
          </Link>
        </Magnetic>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <ProblemSection />
      <BigIdea />
      <WhatWeBuild />
      <WorkPreview />
      <RoiCalculator />
      <WhyUs />
      <Process />
      <FinalCta />
    </main>
  );
}
